<template>
    <div>
        <v-inline-loader v-if="isLoading"/>
        <template v-if="!isCompareEnabled">
            <div :class="contentClass">
                <v-radio
                    class="stack-s"
                    :label="positiveLabel"
                    :value="false"
                    :group="stateGroupName"
                    :model="isNegativeCheck"
                    @update="toggleNegativeCheck"
                />
                <v-radio
                    :label="negativeLabel"
                    :value="true"
                    :group="stateGroupName"
                    :model="isNegativeCheck"
                    @update="toggleNegativeCheck"
                />
            </div>
            <div :class="contentClass">
                <div @click="toggleValueInput">
                    <v-error-messages :has-frontend-errors="$v.$error" class="stack-m">
                        <li class="list-item">Please provide at least one value.</li>
                    </v-error-messages>
                    <v-select
                        v-if="isStaticList"
                        :options="staticOptions"
                        :validate="$v.values"
                        v-model="values"
                        allow-empty
                        multiple/>
                    <v-select
                        v-else-if="hasAutoCompletion"
                        @input="setAsyncValues"
                        :async-find="triggerAutoCompletion"
                        :async-get-init-value="getInitValues"
                        :loading="isAutoCompleteLoading"
                        :validate="$v.values"
                        :value="values"
                        label="Select an option or type to search"
                        clear-on-select
                        multiple
                        taggable
                        allow-empty
                        placeholder="Type to search"
                    />
                    <r-input
                        v-else
                        help-text="Enter one or more comma separated values."
                        :disabled="isListEnabled"
                        placeholder="Enter values"
                        :value="inlineValues"
                        @input="updateInline"
                        :validate="$v.values"
                        :multiline="true"
                        rows="1"
                    />
                </div>
                <div class="divider has-or">&nbsp;&nbsp;or&nbsp;&nbsp;</div>
                <!-- Value list -->
                <v-checkbox
                    :class="{'stack-m': isListEnabled}"
                    label="Compare to an existing list of values"
                    :value="true"
                    @input="toggleCsvFromList"
                    :disabled="hasNoListOptions"
                    v-model="isListEnabled"
                />
                <div v-if="isListEnabled">
                    <v-select
                        :preselect-first="true"
                        :options="listOptions"
                        v-model="listId"
                        placeholder="Select a predefined list"
                    />
                </div>
            </div>
            <!-- Empty value -->
            <div
                :class="contentClass">
                <div :class="{'stack-m': isEmptyEnabled}">
                    <v-checkbox
                        label="Check for an empty value"
                        :value="true"
                        v-model="isEmptyEnabled"
                    />
                </div>
                <div v-if="isEmptyEnabled">
                    <v-radio
                        class="stack-s"
                        label="Field is empty"
                        :value="true"
                        :group="emptyGroupName"
                        :model="isEmpty"
                        @update="toggleEmpty"
                    />
                    <v-radio
                        label="Field is not empty"
                        :value="false"
                        :group="emptyGroupName"
                        :model="isEmpty"
                        @update="toggleEmpty"
                    />
                </div>
            </div>
            <!-- CSV upload -->
            <div
                :class="[contentClass, !isListEnabled && useCsv ? 'bottom-borderless' : '']">
                <v-checkbox
                    label="Using a CSV file"
                    :value="true"
                    :disabled="isListEnabled"
                    v-model="useCsv"/>
            </div>
            <div :class="menuContentClass" v-if="useCsv && !isListEnabled">
                <v-file-selector
                    ref="file-selector"
                    @input="parseFileSource"
                    @cancel="cancelFileProcess">
                    <template slot-scope="{openFileBrowser, setFocus}">
                        <div class="dropdown-item"
                             @click="openFileBrowser"
                             @click.prevent="handleSelectFileImportType('add')"
                             @focus="setFocus">
                            <v-icon icon="sticky-note" class="inline-s"/>
                            <span>Add values from file</span>
                        </div>
                        <div class="dropdown-item"
                             @click="openFileBrowser"
                             @click.prevent="handleSelectFileImportType('replace')"
                             @focus="setFocus">
                            <v-icon icon="sticky-note" class="inline-s"/>
                            <span>Replace values with file contents</span>
                        </div>
                    </template>
                </v-file-selector>
                <div class="dropdown-item" @click="downloadCSV">
                    <v-icon icon="sticky-note" class="inline-s"/>
                    <span>Download values as a CSV file</span>
                </div>
            </div>
            <div :class="menuContentClass">
                <div class="dropdown-item" @click="showModal = true">
                    <v-icon icon="custom-fields" class="inline-s"/>
                    <span>Compare to another field</span>
                </div>
            </div>
        </template>
        <!-- Path Comparison -->
        <template v-else>
            <div :class="contentClass">
                <span class="chip chip-secondary stack-m">
                    <condition-item-summary
                        :field-provider="fieldProvider"
                        :condition="baseCondition"/>
                </span>
                <div class="stack-m">
                    <v-radio
                        class="stack-s"
                        label="Is same as"
                        :value="false"
                        :group="comparisonGroupName"
                        :model="isNegativeComparison"
                        @update="toggleNegativeComparison"
                    />
                    <v-radio
                        label="Is not same as"
                        :value="true"
                        :group="comparisonGroupName"
                        :model="isNegativeComparison"
                        @update="toggleNegativeComparison"
                    />
                </div>
                <span class="chip chip-secondary">
                    <condition-item-summary
                        :field-provider="fieldProvider"
                        :condition="compareCondition"/>
                </span>
            </div>
            <div :class="contentClass">
                <r-button size="small" fluid @click="cancelComparison">Cancel Comparison</r-button>
            </div>
        </template>
        <!-- Footer -->
        <filter-footer
            @apply-filter="applyFilter"
            @cancel-filter="cancelFilter"/>
        <!-- Path Comparison Modal -->
        <v-modal v-if="showModal" title="Compare to another field" @close="closeComparisonModal" class="contact-modal">
            <div slot="content">
                <criteria-input
                    :field-provider="fieldProvider"
                    @select-field="selectComparisonField"
                    :class="{'stack-m': interComparePath}"/>
                <div v-if="interComparePath">
                    <p class="stack-s">Compare to</p>
                    <span class="chip">
                        <condition-item-summary
                            :field-provider="fieldProvider"
                            :condition="compareCondition"/>
                    </span>
                </div>
            </div>
            <template slot="actions">
                <r-button
                    type="primary"
                    :disabled="!interComparePath"
                    @click="applyComparison">Apply
                </r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import {
        vCheckbox, vRadio, vSelect, vIcon, vErrorMessages, vFileSelector,
    } from '@/components/form';
    import AppWorker from '@/common/worker';
    import BaseFilterMixin from '@/modules/rules-engine/components/condition-builder/filters/base-filter-mixin';
    import ConditionOperators from '@/components/query/spec/condition-operators';
    import CriteriaCondition from '@/components/query/spec/criteria-condition';
    import CriteriaInput from '@/modules/rules-engine/components/condition-builder/criteria-input';
    import FilterFooter from '@/modules/rules-engine/components/condition-builder/filters/filter-footer';
    import ListModel from '@/common/models/list-model';
    import Media from '@/common/files/media';
    import mergeCSVImportResultsData from '@/modules/rules-engine/helpers/merge-csv-import-results-data';
    import prepareCSVImportResults from '@/modules/rules-engine/helpers/prepare-csv-import-results';
    import shordid from 'shortid';
    import vInlineLoader from '@/components/v-inline-loader';
    import vModal from '@/components/modal/v-modal';
    import {createDialog, vConfirm} from '@/components/modal/dialogs/index';
    import {entityToNamespace} from '@/common/api/definitions/entities';
    import {NormalizedCondition} from '@/components/query/adapters/criteria/normalized-criteria';
    import {required} from 'vuelidate/lib/validators';
    import {COUNTRIES_LIST} from '@/data/countries';
    import {CURRENCIES_LIST} from '@/data/currencies';
    import pasteToCommaString from '@/common/helpers/paste-to-comma-string';

    export default {
        name: 'text-filter',
        mixins: [BaseFilterMixin],
        components: {
            CriteriaInput,
            FilterFooter,
            ConditionItemSummary: () => import('@/modules/rules-engine/components/condition-summary/condition-item-summary'),
            vCheckbox,
            vErrorMessages,
            vFileSelector,
            vIcon,
            vInlineLoader,
            vModal,
            vRadio,
            vSelect,
        },
        computed: {
            groupName() {
                return this.condition.getPath();
            },
            stateGroupName() {
                return `${this.groupName}/state`;
            },
            emptyGroupName() {
                return `${this.groupName}/empty`;
            },
            comparisonGroupName() {
                return `${this.groupName}/comparison`;
            },
            listOptions() {
                if (!this.isLoading) {
                    return this.$store.getters['rulesEngine/listOptions'];
                }
                return [];
            },
            hasNoListOptions() {
                return this.listOptions.length === 0;
            },
            positiveLabel() {
                if (this.isListEnabled) {
                    return 'In this predefined list';
                }
                return 'In these values';
            },
            negativeLabel() {
                return `Not ${this.positiveLabel.toLowerCase()}`;
            },
            hasValues() {
                return this.values ? this.values.length > 0 : false;
            },
            isStaticList() {
                return ['currency', 'country', 'bankCountry'].includes(this.field.name);
            },
            staticOptions() {
                if (this.isStaticList) {
                    if (this.field.name === 'currency') {
                        return CURRENCIES_LIST;
                    }
                    return COUNTRIES_LIST;
                }
                return [];
            },
        },
        data() {
            // flatten the recursive state of the condition
            // for easier use
            const values = this.getInitialValues();
            return {
                isEmptyEnabled: this.condition.isUnknown,
                isListEnabled: this.condition.isValueList,
                isCompareEnabled: this.condition.isComparison,
                isEmpty: !this.condition.isNegativeUnknown,
                isLoading: true,
                useCsv: false,
                showModal: false,
                baseCondition: new NormalizedCondition(new CriteriaCondition({path: this.condition.fullPath})),
                interComparePath: null,
                comparePath: this.condition.comparePath || null,
                isNegativeComparison: this.condition.isNegativeComparison === undefined
                    ? false // default value
                    : this.condition.isNegativeComparison,
                compareCondition: this.condition.comparePath
                    ? new NormalizedCondition(new CriteriaCondition({path: this.condition.comparePath}))
                    : null,
                listId: this.condition.listId || null,
                values,
                inlineValues: Array.isArray(values) ? values.join(', ') : null,
                isNegativeCheck: this.getInitialNegationState(),
                // CSV handling
                file: null,
                fileImportStrategy: null,
                fileErrors: [],
                // defines the threshold at which we decide to create a list
                // for the user automatically
                listCountThreshold: 100,
                // used for list generation and CSV file ID
                generatedListId: shordid.generate(),
                isAutoCompleteLoading: false,
            };
        },
        validations() {
            if (!this.isListEnabled && !this.isEmptyEnabled) {
                return {
                    values: {required},
                };
            }
            return {};
        },
        methods: {
            getInitialValues() {
                if (this.condition.listData) {
                    return this.condition.listData.values;
                }
                return this.condition.data.values || [];
            },
            getInitialNegationState() {
                if (this.condition.isNegativeList !== undefined) {
                    return this.condition.isNegativeList;
                }
                return this.condition.isNegative === true;
            },
            toggleEmpty(state) {
                this.isEmpty = state;
            },
            toggleNegativeCheck(state) {
                this.isNegativeCheck = state;
            },
            toggleNegativeComparison(state) {
                this.isNegativeComparison = state;
            },
            toggleValueInput() {
                if (this.isListEnabled) {
                    this.isListEnabled = false;
                }
            },
            toggleCsvFromList(listEnabled) {
                if (listEnabled) {
                    this.useCsv = false;
                }
            },
            async loadLists() {
                this.isLoading = true;
                await this.$store.dispatch('rulesEngine/fetchLists');
                this.isLoading = false;
            },
            selectComparisonField({field, precursor, definition}) {
                const condition = new CriteriaCondition({
                    path: precursor,
                    op: ConditionOperators.contains,
                    condition: new CriteriaCondition({path: `/${field}`}),
                });
                const normalized = new NormalizedCondition(condition, {
                    summary: true,
                    definition,
                });
                this.interComparePath = normalized.getPath();
                // create dummy condition from path to trigger summary render
                this.compareCondition = normalized;
            },
            applyComparison() {
                this.comparePath = this.interComparePath;
                this.closeComparisonModal();
                this.isCompareEnabled = true;
            },
            cancelComparison() {
                this.isCompareEnabled = false;
                this.interComparePath = null;
            },
            closeComparisonModal() {
                this.interComparePath = null;
                this.showModal = false;
            },
            /**
             * Update the normalized condition before sending it up the ancestor chain
             */
            applyFilter() {
                // TODO verify usefulness of this flag
                this.condition.isComplex = true;
                this.condition.isComparison = this.isCompareEnabled;
                if (this.isCompareEnabled) {
                    this.condition.comparePath = this.comparePath;
                    this.condition.isNegativeComparison = this.isNegativeComparison;
                } else {
                    this.condition.isUnknown = this.isEmptyEnabled;
                    if (this.isEmptyEnabled) {
                        this.condition.isNegativeUnknown = !this.isEmpty;
                    }
                    this.condition.isValueList = this.isListEnabled;
                    if (this.isListEnabled) {
                        this.condition.isNegative = this.isNegativeCheck;
                        this.condition.listId = this.listId;
                        this.condition.listData = null;
                    } else {
                        // validate values entered manually
                        this.$v.$touch();
                        if (this.$v.$error) {
                            return;
                        }
                        if (!this.condition.listData) {
                            this.condition.listData = {};
                        }
                        this.condition.listData.values = this.values;
                        this.condition.isNegativeList = this.isNegativeCheck;
                    }
                }
                this.$emit('apply-filter', {
                    index: this.index,
                    condition: this.condition,
                });
            },
            /* CSV Handling */
            resetFile() {
                this.fileImportStrategy = null;
                this.file = null;
                this.fileErrors = [];
                this.values = [];

                /**
                 * you should reset input file value,
                 * in case if you are changed type and not changed file
                 */
                this.$refs['file-selector'].resetFileValue();
            },
            /**
             * If user select type and then cancel load file
             * you should reset dropdown value
             */
            cancelFileProcess() {
                /**
                 * if user already imported file (or added some values), or it is edit page
                 * and then user click "import", then cancel - you can't reset values
                 * you should leave the previous one
                 */
                if (this.values.length) {
                    return;
                }
                this.resetFile();
            },
            /**
             * @param {FileList} fileList file value
             * @returns {Promise<void>}
             */
            async parseFileSource(fileList) {
                if (!fileList.length) {
                    this.resetFile();
                    return;
                }

                // eslint-disable-next-line
                this.file = fileList[0];

                // show notification if it's big file
                if (this.file.size > 1000000) {
                    this.$toast.positive(`Starting to parse the file, please wait.`);
                }

                const output = await AppWorker.csv.parse(this.file);

                // valid file, update values
                if (output.isValid) {
                    this.updateValuesByCSVOutput(output);
                    return;
                }

                // invalid file type or etc. need confirm
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Wrong File',
                    content: `Selected file ${this.file.name}
                         does not seem to be a valid CSV format.
                         The data might not be read correctly.
                         Do you wish to continue with the import?`,
                    confirmLabel: 'Yes',
                };

                const isConfirmed = await confirmDialog(dialogParams);

                // reset file
                if (!isConfirmed) {
                    this.resetFile();
                    return;
                }

                // update values
                this.updateValuesByCSVOutput(output);
            },
            async updateValuesByCSVOutput(output) {
                this.isLoading = true;
                const results = await prepareCSVImportResults(output);
                const data = await mergeCSVImportResultsData(
                    this.fileImportStrategy,
                    this.values,
                    results.data,
                );

                if (data.values.length >= this.listCountThreshold) {
                    await this.createAndUseList(data.values);
                } else {
                    // update values
                    this.values = data.values;
                    // notify errors
                    this.fileErrors = output.errors;
                }
                if (this.fileErrors.length) {
                    this.$toast.negative(`Found ${this.fileErrors.length} errors`);
                }
                if (data.removed) {
                    this.$toast.warning(`Removed ${data.removed} values`);
                }
                if (data.added) {
                    this.$toast.positive(`Added ${data.added} values`);
                }
                this.isLoading = false;
                this.useCsv = false;
            },
            async createAndUseList(values) {
                // create a list and use it
                const description = `Rule list (${this.generatedListId})`;
                const data = new ListModel({
                    description,
                    values,
                });
                const {fields} = await this.$api(({lists}) => lists.create({data}));
                this.$store.dispatch('rulesEngine/invalidateLists');
                await this.$store.dispatch('rulesEngine/fetchLists');
                this.isListEnabled = true;
                this.listId = fields.id;
                this.$toast.positive(`Created the list "${description}" from the file you provided.`, {delay: 10000});
            },
            handleSelectFileImportType(fileImportStrategy) {
                this.fileImportStrategy = fileImportStrategy;
            },
            downloadCSV() {
                if (this.hasValues) {
                    const data = this.values.reduce((result, row) => {
                        result += `${row}\n`;
                        return result;
                    }, '');

                    const media = new Media({
                        mimeType: 'text/csv',
                        data,
                        fileName: `rule-list-${this.generatedListId}.csv`,
                    });

                    media.triggerDownload();
                } else {
                    this.$toast.negative('There are no values to download');
                }
            },
            async triggerAutoCompletion(query = '', params) {
                const {
                    field, entity, identity, pick,
                } = this.field.autoComplete;
                const target = pick || field;
                this.isAutoCompleteLoading = true;
                if (entity in entityToNamespace) {
                    const namespace = entityToNamespace[entity];
                    try {
                        const {items} = await this.$api(client => client[namespace].getAll({...params, q: query}));
                        const mapper = identity || (({fields}) => ({
                            label: fields[target],
                            value: fields[target],
                        }));
                        return items.map(mapper);
                    } catch (error) {
                        return null;
                    } finally {
                        this.isAutoCompleteLoading = false;
                    }
                }
                this.isAutoCompleteLoading = false;
                return null;
            },
            async getInitValues(currentValues = []) {
                return currentValues.map(v => ({
                    label: v,
                    value: v,
                }));
            },
            setAsyncValues(v) {
                this.values = v;
            },
            updateInline(data) {
                ({
                    inline: this.inlineValues,
                    values: this.values,
                } = pasteToCommaString(data));
            },
        },
        async mounted() {
            await this.loadLists();
        },
    };
</script>

<style scoped>
    .dropdown-content.bottom-borderless {
        border-bottom: 0;
        padding-bottom: 0;
    }
</style>
